/**
 * chat-sync — the pure pieces of the chat state machine (useAstrologerChat) and the two small reads
 * it uses to find out what the server really did: the stored thread and the star wallet.
 */
import type { QueryClient } from '@tanstack/react-query';
import { apiFetch } from './api-client';
import { ApiError, ensureOk } from './api-error';
import type { ChatErrorCode } from './chat-sse';

/** `unsent`: a question the server did not store (rejected before it was saved) — left out of the count. */
export type ChatMsg = { role: 'user' | 'assistant'; content: string; unsent?: true; requestId?: string | null; billing?: 'pending' | 'paid' | 'failed' };
export interface ChatHeader { name: string | null; relation: string | null; isSelf: boolean; sex: string | null }
export type ChatLoadError = 'chart_not_found' | 'chart_not_ready' | 'not_found' | 'network';

export interface ChartRef { id: string; name: string | null; relation: string | null; isSelf: boolean; sex?: string | null }
export interface OpenerResponse { opener: string; chart: ChartRef | null }
export interface ThreadResponse {
  surface?: string;
  id: string;
  chartId: string | null;
  chart: ChartRef | null;
  title: string | null;
  messages: Array<{ role: 'user' | 'assistant'; content: string; createdAt?: string }>;
}

/** Rejected before the server stored the question (B1 contract: «nothing stored or charged»). */
export const NOT_STORED: ReadonlySet<ChatErrorCode> = new Set<ChatErrorCode>([
  'quote_changed', 'answer_changed', 'version_conflict', 'chat_disabled', 'course_locked',
  'rate', 'in_flight', 'message_too_long', 'bad_request', 'chart_not_found', 'not_found', 'chart_not_ready',
]);
/** The chart (or the conversation with it) is gone: nothing more can be asked here. */
export const GONE: ReadonlySet<ChatErrorCode> = new Set<ChatErrorCode>(['chart_gone', 'chart_not_found', 'not_found']);

/** The sex of a saved chart from the list / chart caches (the thread answer does not carry it). */
export function cachedSex(qc: QueryClient, id: string): string | null {
  const one = qc.getQueryData<{ sex?: string | null }>(['chart', id]);
  if (one?.sex) return one.sex;
  const list = qc.getQueryData<{ charts?: Array<{ id: string; sex?: string | null }> }>(['charts']);
  return list?.charts?.find((c) => c.id === id)?.sex ?? null;
}

/** The opener fires server telemetry (`chat_opened`) on every 200 → ask once per screen; a thread likewise. */
export const ASK_ONCE = {
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  // Never refetch a loaded answer on mount; a cached ERROR is asked again (e.g. «карта ещё считается»).
  refetchOnMount: (q: { state: { status: string } }) => q.state.status === 'error',
  retry: false,
} as const;

/** Wallet polling after a paid top-up. */
export const STAR_CREDIT_POLL_MS = 1000;
export const STAR_CREDIT_WAIT_MS = 10_000;
/** Waiting for an answer the server is still writing after a dropped stream. */
export const RECONCILE_POLL_MS = 3000;
export const RECONCILE_WAIT_MS = 45_000;

/** Questions the server has (or will have) stored: unsent ones are left out. */
export const countUsers = (m: ChatMsg[]): number => m.filter((x) => x.role === 'user' && !x.unsent).length;

/**
 * The stored thread holds the answer to the screen's last question: it has at least as many
 * questions as the screen (`localUsers`) and an assistant message (even an empty one) after its last
 * question. By count, never by text — the same «Да» can be asked twice.
 */
export function isAnswered(stored: ChatMsg[], localUsers: number): boolean {
  const lastUser = stored.map((m) => m.role).lastIndexOf('user');
  return countUsers(stored) >= localUsers && lastUser >= 0 && stored.slice(lastUser + 1).some((m) => m.role === 'assistant');
}

export const SELF_HEADER: ChatHeader = { name: null, relation: 'SELF', isSelf: true, sex: null };
const LANGS = ['ru', 'en', 'uk'] as const;
export const pickLang = (l: string | undefined): string =>
  ((LANGS as readonly string[]).includes(l ?? '') ? (l as string) : 'en');

export const toHeader = (chart: ChartRef | null, withSex: boolean): ChatHeader =>
  chart
    ? { name: chart.name, relation: chart.relation, isSelf: chart.isSelf, sex: withSex ? (chart.sex ?? null) : null }
    : SELF_HEADER;

export function loadErrorOf(err: unknown, kind: 'new' | 'thread'): ChatLoadError {
  if (err instanceof ApiError) {
    if (kind === 'new' && err.status === 404) return 'chart_not_found';
    if (kind === 'new' && err.status === 409) return 'chart_not_ready';
    if (kind === 'thread' && err.status === 404) return 'not_found';
  }
  return 'network';
}

/** `GET /api/v1/stars/wallet` → the balance, or null when it cannot be read. */
export async function fetchWalletBalance(jwt: string): Promise<number | null> {
  try {
    const res = await apiFetch('/api/v1/stars/wallet', jwt);
    if (!res.ok) return null;
    const b = ((await res.json()) as { balance?: unknown }).balance;
    return typeof b === 'number' ? b : null;
  } catch {
    return null;
  }
}

/**
 * `GET /api/v1/chat/threads/:id` → the stored history (no createdAt), or null when it cannot be read.
 * Note: the server fires `chat_thread_resumed` on every call.
 */
export async function fetchStoredThread(jwt: string, id: string, signal?: AbortSignal): Promise<ChatMsg[] | null> {
  try {
    const res = await ensureOk(await apiFetch(`/api/v1/chat/threads/${encodeURIComponent(id)}`, jwt, { signal }));
    const data = (await res.json()) as ThreadResponse;
    return data.messages.map(({ role, content }) => ({ role, content }));
  } catch {
    return null;
  }
}

/** Cache scope only, never authentication: the server verifies the JWT on every request. */
export function chatSessionUserId(jwt: string | null, knownId?: string | null): string | undefined {
  if (knownId) return knownId;
  try {
    const encoded = jwt?.split('.')[1];
    if (!encoded) return undefined;
    const payload = JSON.parse(atob(encoded.replace(/-/g, '+').replace(/_/g, '/'))) as { userId?: unknown };
    return typeof payload.userId === 'string' && payload.userId.length > 0 && payload.userId.length <= 100 ? payload.userId : undefined;
  } catch { return undefined; }
}
